import * as React from 'react';
import { Redirect } from 'react-router';
import * as _ from 'underscore';

import { connect } from 'react-redux';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { IGroupDetailsCtrl } from '../../../ts/controllers/GroupDetailsCtrl';
import { IGroupUsersSubplaceScope } from '../../../ts/controllers/GroupUsersSubplaceCtrl';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { IGetGroupDetailInfoResult, IGroupInfo } from '../../../ts/models/IGroupInfo';
import { Groups } from '../../../ts/resources/Groups';
import { ServerService } from '../../../ts/services/ServerService';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { SpaceInfo } from '../../state/SpaceInfo';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { SpacesSiteUrls } from '../SpacesSiteUrls';

export interface GroupSubspaceProviderOwnProps extends ReactDirectiveProps {}

export interface GroupSubspaceProviderStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
}

interface GroupSubspaceProverPropsFromAngular {
  readonly groups: Groups;
  readonly serverService: ServerService;
}

export type GroupSubspaceNonAngularProps = GroupSubspaceProviderOwnProps & GroupSubspaceProviderStateProps;

export type GroupSubspaceProviderContainerProps = GroupSubspaceProviderOwnProps & GroupSubspaceProviderStateProps & GroupSubspaceProverPropsFromAngular;

export interface GroupSubspaceProviderState {
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly groupDetails: IGetGroupDetailInfoResult;
}

const usersTemplate = `<div ng-controller="GroupUsersSubplaceCtrl" ng-include="'groupUsersSubplace.html'"></div>`;
const detailsTemplate = `<div ng-controller="GroupDetailsCtrl" ng-include="'groupDetails.html'"></div>`;

export class GroupSubspaceProviderContainer extends React.PureComponent<GroupSubspaceProviderContainerProps, GroupSubspaceProviderState> {

  private subSpaceScope: IGroupUsersSubplaceScope | IGroupDetailsCtrl = null;
  private unmounted: boolean = false;

  public constructor(props: GroupSubspaceProviderContainerProps) {
    super(props);
    this.state = {renderChildren: false, hasError: false, groupDetails: undefined};
  }

  public render() {
    if (this.state.renderChildren) {
      return this.renderSubSpace();
    } else if (this.state.hasError) {
       return <Redirect to={this.props.urls.groupUsersUrl(this.props.spaceInfo.item.id)} />;
    } else {
      return null;
    }
  }

  static getDerivedStateFromProps(nextProps: GroupSubspaceProviderContainerProps, prevState: GroupSubspaceProviderState): Partial<GroupSubspaceProviderState> {
    // if we are in details page and we don't have details, don't render anything since we don't have the data we need
    if (nextProps.spaceInfo.subspaceName === 'details' && _.isUndefined(prevState.groupDetails)) {
      return {renderChildren: false};
    }
    return {};
  }

  public componentDidMount() {
    this.getData();
    if (this.haveAllDataNeededToRender()) {
      this.updateState({renderChildren: true}, () => {
        // defer it so it doesn't interfere with a previous apply
        // this seems to be needed so that angular always plays nice
        _.defer(() => {
          this.subSpaceScope.$apply();
        });
      });
    }
  }

  public componentDidUpdate(prevProps: GroupSubspaceProviderContainerProps) {

    // if we dont have all we need - attempt to get the needed data
    if (!this.haveAllDataNeededToRender()) {
      if (!this.state.renderChildren) {
        this.getData();
      }
      return;
    }

    // The angular directives don't play nice.
    // a scope apply here gets them to re-render with updated content correctly
    if (this.state.renderChildren === false) {
      this.updateState({renderChildren: true}, () => {
        this.subSpaceScope.$apply();
      });
    } else if (prevProps.spaceInfo.subspaceName !== this.props.spaceInfo.subspaceName) {
      this.subSpaceScope.$apply();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private haveAllDataNeededToRender() {
    return this.props.spaceInfo.subspaceName === 'users' || !_.isUndefined(this.state.groupDetails);
  }

  private getData = async () => {
    if (this.props.spaceInfo.subspaceName === 'details') {
      try {
        const groupDetails = await this.props.groups.getGroupDetails(this.props.spaceInfo.item.id);
        this.updateState({groupDetails});
      } catch (e) {
        this.updateState({hasError: true});
      }
    }
  }

  private updateState<K extends keyof GroupSubspaceProviderState>(newState: Pick<GroupSubspaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private renderSubSpace = () => {
    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    if (this.props.spaceInfo.subspaceName === 'users') {
      return (
        <div>
          <AngularDirective
            template={usersTemplate}
            angularContext={this.props.angularContext}
            setupScope={this.setupScopeForUsersSubspace}
          />
        </div>
      );
    }
    else if (this.props.spaceInfo.subspaceName === 'details') {
      return (
        <div>
          <AngularDirective
            template={detailsTemplate}
            angularContext={this.props.angularContext}
            setupScope={this.setupScopeForDetailsSubspace}
          />
        </div>
      );
    }
    else {  // this happens when navigating away from the group page
      return null;
    }
  }

  private setupScopeForUsersSubspace = (scope: ng.IScope) => {
    const childScope = scope.$new() as IGroupUsersSubplaceScope;
    this.subSpaceScope = childScope;
    childScope.group = this.props.spaceInfo.item as unknown as IGroupInfo;
    return childScope;
  }

  private setupScopeForDetailsSubspace = (scope: ng.IScope) => {
    const childScope = scope.$new() as IGroupDetailsCtrl;
    this.subSpaceScope = childScope;
    childScope.groupDetails = {...this.state.groupDetails};
    childScope.canSessionUserManageUsers = this.props.serverService.canSessionUserManageUsers();
    return childScope;
  }
}

export namespace GroupSubspaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    const groups = ServiceLocator.get<Groups>('Groups');
    const serverService = ServiceLocator.get<ServerService>('ServerService');
    return {
      groups,
      serverService,
    };
  }

  function selectPropsFromAngularServices(angularServices: GroupSubspaceProverPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<GroupSubspaceNonAngularProps, GroupSubspaceProverPropsFromAngular, GroupSubspaceProverPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const GroupSubspaceProviderAng: React.ComponentType<GroupSubspaceNonAngularProps> = GroupSubspaceProviderAngularProvider.HOC(GroupSubspaceProviderContainer);

const mapStateToProps: MapStateToProps<GroupSubspaceProviderStateProps> = (state: AppState) => ({
  spaceInfo: selectSpaceInfo(state),
  urls: siteUrlsSelector(state)
});

export const ConnectedGroupSubspaceProviderContainer: React.ComponentClass<GroupSubspaceProviderOwnProps>
  = connect(mapStateToProps)(GroupSubspaceProviderAng);

export type GroupSubspaceProviderProps = GroupSubspaceProviderOwnProps;
export function GroupSubspaceProvider(props: GroupSubspaceProviderProps) {
  return <ConnectedGroupSubspaceProviderContainer {...props} />;
}
